/**
 * UTM Parameter Capture Utility
 *
 * Captures UTM parameters from the URL on first visit and stores them
 * in sessionStorage so they persist through the signup flow.
 * On signup, these are attached to the user profile for attribution.
 */

const UTM_STORAGE_KEY = 'heseda_utm';

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer?: string;
  landing_page?: string;
  captured_at?: string;
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

/**
 * Capture UTM params from the current URL and store in sessionStorage.
 * Only captures on the first page load that has UTM params — does not overwrite.
 */
export function captureUtmParams(): void {
  if (typeof window === 'undefined') return;

  // Don't overwrite if already captured this session
  if (sessionStorage.getItem(UTM_STORAGE_KEY)) return;

  const params = new URLSearchParams(window.location.search);
  const hasUtm = UTM_KEYS.some(key => params.has(key));

  if (!hasUtm && !document.referrer) return;

  const utm: UtmParams = {
    landing_page: window.location.pathname,
    captured_at: new Date().toISOString(),
  };

  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) utm[key] = val;
  }

  // Capture referrer if coming from external site
  if (document.referrer && !document.referrer.includes('heseda.ai')) {
    utm.referrer = document.referrer;
  }

  sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
}

/**
 * Retrieve stored UTM params. Returns null if none captured.
 */
export function getStoredUtmParams(): UtmParams | null {
  if (typeof window === 'undefined') return null;
  const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as UtmParams;
  } catch {
    return null;
  }
}

/**
 * Clear stored UTM params (call after attribution is recorded).
 */
export function clearStoredUtmParams(): void {
  if (typeof window === 'undefined') return;
  sessionStorage.removeItem(UTM_STORAGE_KEY);
}
